import { useState, useMemo } from "react";
import { Layout, Menu, Typography, Button, Select } from "antd";
import {
  DoubleLeftOutlined,
  DoubleRightOutlined,
  ArrowLeftOutlined,
  MessageOutlined,
  HomeOutlined,
  ApiOutlined,
} from "@ant-design/icons";
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";

import { useUser } from "@/contexts/UserContext";
import { usePromotedTrials } from "@/hooks/usePromotedTrials";
import LogoIcon from "@/assets/icons/latest_vivo_logo.svg?react";
import VivoLogo from "@/assets/icons/vivo_logo.svg?react";
import ButtonMenu from "@/components/UI/ButtonMenu";
import UserMenu from "@/components/UI/UserMenu";
import { HEADER_HEIGHT } from "@/utils/tableHelpers";

const { Sider, Header, Content } = Layout;
const { Text } = Typography;

const TABS = ["home", "connections", "askvivo"] as const;

export default function PlannedTrialShell() {
  const [collapsed, setCollapsed] = useState(false);
  const { planId } = useParams<{ planId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { userInfo } = useUser();
  const { trials } = usePromotedTrials();

  const trial = trials.find((t) => t.planId === planId);

  const activeKey = location.pathname.split("/").at(-1) ?? "";
  const currentTab = (TABS as readonly string[]).includes(
    activeKey as (typeof TABS)[number],
  )
    ? activeKey
    : "home";

  const base = `/planned/${planId}`;
  const firstInitial = userInfo?.first_name?.charAt(0) ?? "?";

  // Build the same trial selector options as TrialHome
  const trialSelectorOptions = useMemo(() => {
    const visibleTrials = userInfo?.trials ?? [];

    const regularOptions = visibleTrials.map((t) => ({
      label: `${t.name ?? t.id} | ${t.id}`,
      value: `trial:${t.id}`,
    }));

    const plannedOptions = trials.map((t) => ({
      label: t.name,
      value: `planned:${t.planId}`,
    }));

    const hasPortfolio = (userInfo?.trials?.length ?? 0) > 1;
    const portfolioOptions = hasPortfolio
      ? [
          {
            label: "Portfolio",
            value: "portfolio",
          },
          {
            label: <hr style={{ margin: "4px 0", borderColor: "#E5E7EB" }} />,
            value: "__divider__",
            disabled: true,
          },
        ]
      : [];

    return [...portfolioOptions, ...regularOptions, ...plannedOptions];
  }, [userInfo?.trials, trials]);

  const currentSelectValue = `planned:${planId}`;

  const handleTrialSelect = (value: string) => {
    if (value.startsWith("planned:")) {
      navigate(`/planned/${value.slice(8)}/home`);
    } else if (value === "portfolio") {
      navigate("/portfolio/trials");
    } else if (value.startsWith("trial:")) {
      navigate(`/trial/${value.slice(6)}`);
    }
  };

  const menuItems = [
    {
      key: "askvivo",
      icon: <MessageOutlined />,
      label: <Link to={`${base}/askvivo`}>Ask Vivo</Link>,
    },
    { type: "divider" as const },
    {
      key: "home",
      icon: <HomeOutlined />,
      label: <Link to={`${base}/home`}>Home</Link>,
    },
    {
      key: "data",
      icon: <ApiOutlined />,
      label: "Data",
      children: [
        {
          key: "connections",
          label: <Link to={`${base}/connections`}>Connections</Link>,
        },
      ],
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={256}
        collapsible
        trigger={null}
        collapsed={collapsed}
        onCollapse={setCollapsed}
        style={{ height: "100vh", position: "fixed" }}
        collapsedWidth={80}
      >
        <div
          className={`flex flex-col h-full py-3 overflow-y-auto ${collapsed ? "px-5" : "px-6"}`}
        >
          {/* Logo */}
          <div>
            {collapsed ? (
              <div className="flex flex-col items-center mt-[24px] mb-3">
                <Link to="/dashboard">
                  <VivoLogo className="w-[32px] h-[18px]" />
                </Link>
              </div>
            ) : (
              <div className="flex flex-col mt-[24px] mb-3">
                <Link to="/dashboard">
                  <LogoIcon />
                </Link>
              </div>
            )}
          </div>

          {/* Trial selector */}
          {!collapsed && (
            <div className="mt-3">
              <Select
                className="w-full"
                options={trialSelectorOptions}
                value={currentSelectValue}
                onSelect={handleTrialSelect}
              />
            </div>
          )}

          {/* Nav menu */}
          <div
            className={`flex-1 overflow-y-auto hide-scrollbar text-white ${collapsed ? "mt-2" : "mt-5"}`}
          >
            <Menu
              theme="dark"
              mode="inline"
              inlineIndent={3}
              selectedKeys={[currentTab]}
              defaultOpenKeys={["data"]}
              className="trial-main-nav"
              classNames={{ popup: { root: "trial-main-nav-popup" } }}
              tooltip={{ classNames: { root: "trial-main-nav-tooltip" } }}
              items={menuItems}
            />

            {/* Back to My Trials */}
            <div className="flex flex-col">
              <Link to="/dashboard">
                {collapsed ? (
                  <div className="flex flex-col items-center text-sm mt-3 text-white hover:text-white">
                    <div className="border-t border-[#556C75] p-2">
                      <ArrowLeftOutlined className="mt-1" />
                    </div>
                  </div>
                ) : (
                  <div className="flex text-sm px-1 mt-3 text-white hover:text-white">
                    <div className="flex gap-3 border-t border-[#556C75] p-2 w-full">
                      <ArrowLeftOutlined className="text-base" />
                      <p>My Trials</p>
                    </div>
                  </div>
                )}
              </Link>
            </div>
          </div>

          {/* User menu + collapse toggle */}
          <div>
            {collapsed ? (
              <div className="flex flex-col items-center mb-4">
                <UserMenu mode="navigation">
                  <div className="bg-[#0E98D0] rounded-full h-7.5 w-7.5 flex justify-center items-center text-md border-none">
                    <span className="text-white">{firstInitial}</span>
                  </div>
                </UserMenu>
                <button
                  type="button"
                  onClick={() => setCollapsed(!collapsed)}
                  aria-label="Expand sidebar"
                  className="mt-5 h-10 w-10 shrink-0 flex items-center justify-center bg-[#ffffff1a] rounded-md text-white hover:bg-[#ffffff26] hover:text-white active:bg-[#ffffff33] active:scale-[0.98] transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                >
                  <DoubleRightOutlined className="text-inherit font-semibold" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-5 mb-4">
                <div className="flex-1">
                  <UserMenu mode="navigation">
                    <ButtonMenu userInfo={userInfo} navbar={true} />
                  </UserMenu>
                </div>
                <button
                  type="button"
                  onClick={() => setCollapsed(!collapsed)}
                  aria-label="Collapse sidebar"
                  className="h-10 w-10 shrink-0 flex items-center justify-center bg-[#ffffff1a] rounded-[6px] text-white hover:bg-[#ffffff26] hover:text-white active:bg-[#ffffff33] active:scale-[0.98] transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                >
                  <DoubleLeftOutlined className="text-inherit font-semibold" />
                </button>
              </div>
            )}
          </div>
        </div>
      </Sider>

      <Layout
        className={`bg-[#EFF1F2] relative ${collapsed ? "ml-[80px]" : "ml-[256px]"}`}
        style={{ minHeight: "100vh" }}
      >
        {/* Header */}
        <Header
          className="bg-white px-0 leading-none shadow-sm"
          style={{
            height: `${HEADER_HEIGHT}px`,
            position: "sticky",
            top: 0,
            right: 0,
            zIndex: 200,
            width: "100%",
          }}
        >
          <div className="flex justify-between m-4">
            <div className="flex items-center">
              <Button
                type="primary"
                className="w-[94px] bg-[#8C2760] text-white font-bold leading-6 text-[14px] text-center border-none shadow-none pointer-events-none"
              >
                Planned
              </Button>
              <div className="ml-5">
                <p className="mb-1 text-[16px]">
                  <strong>{trial?.name ?? "Trial"}</strong>
                  {trial?.phase ? ` (${trial.phase})` : ""}
                </p>
                {trial?.description ? (
                  <Text
                    style={{ maxWidth: 500 }}
                    ellipsis={{ tooltip: trial.description }}
                    className="text-[#556C75] text-[13px]"
                  >
                    {trial.description}
                  </Text>
                ) : trial?.indication ? (
                  <p className="text-[#556C75] text-[13px] m-0">
                    {trial.indication}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </Header>

        <Content
          style={{
            background: "#EFF1F2",
            height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
